import { Key, ReactNode } from 'react';
import {
  Box,
  Flex,
  SearchField,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import { getResourceTypeColor } from '@internal/backstage-plugin-catalog-devops-common';
import { useCloudResourcesContext } from '../Providers';

const TypeDot = ({ type }: { type: string }) => (
  <Box
    style={{
      width: 10,
      height: 10,
      borderRadius: 3,
      backgroundColor: getResourceTypeColor(type),
      flexShrink: 0,
    }}
  >
    {null}
  </Box>
);

/** Options carry a live count next to the label; zero-count entries are
 * dimmed (disabled) instead of hidden, unless already selected. */
const withFacetCount = (
  name: string,
  count: number,
  isSelected: boolean,
  leadingIcon?: ReactNode,
) => ({
  id: name,
  label: `${name} (${count})`,
  leadingIcon,
  disabled: count === 0 && !isSelected,
});

/**
 * Toolbar of the resources tab: group-by segments, client-side search and
 * faceted Region/Type (grouped by service) pickers — plus the Account picker
 * on project pages. Replaces the old left filter panel.
 */
export function ResourcesToolbar() {
  const {
    allGroupBy,
    selectedGroupBy,
    setSelectedGroupBy,
    search,
    setSearch,
    allRegions,
    selectedRegions,
    setSelectedRegions,
    allAccounts,
    selectedAccounts,
    setSelectedAccounts,
    allServicesWithTypes,
    selectedTypes,
    selectType,
    unselectType,
    hasRegionFilter,
    hasTypesFilter,
    hasAccountFilter,
    facetCounts,
  } = useCloudResourcesContext();

  // selectService can add duplicate entries, so the picker always works with
  // a deduplicated view.
  const selectedTypesSet = [...new Set(selectedTypes)];

  const syncTypeSelection = (next: string[]) => {
    selectedTypesSet
      .filter(type => !next.includes(type))
      .forEach(type => unselectType(type));
    next
      .filter(type => !selectedTypesSet.includes(type))
      .forEach(type => selectType(type));
  };

  const toKeyList = (value: Key | Key[] | null) =>
    value === null ? [] : (Array.isArray(value) ? value : [value]).map(String);

  const regionCounts = facetCounts('region');
  const typeCounts = facetCounts('type');
  const accountCounts = facetCounts('account');

  const regionOptions = [...allRegions]
    .sort()
    .map(region =>
      withFacetCount(
        region,
        regionCounts[region] ?? 0,
        selectedRegions.includes(region),
      ),
    );

  const typeSections = Object.keys(allServicesWithTypes)
    .sort()
    .map(service => ({
      title: service,
      options: allServicesWithTypes[service]
        .map(typeInfo => typeInfo.name)
        .sort()
        .map(type =>
          withFacetCount(
            type,
            typeCounts[type] ?? 0,
            selectedTypesSet.includes(type),
            <TypeDot type={type} />,
          ),
        ),
    }));

  const accountOptions = [...allAccounts]
    .sort()
    .map(account =>
      withFacetCount(
        account,
        accountCounts[account] ?? 0,
        selectedAccounts.includes(account),
      ),
    );

  const groupByValue = selectedGroupBy[0] ?? 'Flat';

  const handleGroupByChange = (keys: 'all' | Set<Key>) => {
    const key =
      keys === 'all' ? 'Flat' : ([...keys][0] as string | undefined) ?? 'Flat';
    setSelectedGroupBy(key === 'Flat' ? [] : [key]);
  };

  return (
    <Flex
      gap="3"
      align="center"
      style={{ flexWrap: 'wrap' }}
      aria-label="Resources filters"
    >
      <ToggleButtonGroup
        aria-label="Group by"
        selectionMode="single"
        disallowEmptySelection={false}
        selectedKeys={new Set([groupByValue])}
        onSelectionChange={handleGroupByChange}
      >
        {allGroupBy.map(groupBy => (
          <ToggleButton key={groupBy} id={groupBy} size="small">
            {groupBy}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box style={{ width: 280 }}>
        <SearchField
          aria-label="Search resources"
          size="small"
          placeholder="Search resources, types, services…"
          value={search}
          onChange={setSearch}
        />
      </Box>

      {hasRegionFilter && (
        <Box style={{ minWidth: 180 }}>
          <Select
            aria-label="Filter by region"
            size="small"
            placeholder="Region"
            selectionMode="multiple"
            options={regionOptions}
            value={selectedRegions}
            onChange={value => setSelectedRegions(toKeyList(value))}
          />
        </Box>
      )}

      {hasTypesFilter && (
        <Box style={{ minWidth: 180 }}>
          <Select
            aria-label="Filter by type"
            size="small"
            placeholder="Type"
            selectionMode="multiple"
            options={typeSections}
            value={selectedTypesSet}
            onChange={value => syncTypeSelection(toKeyList(value))}
          />
        </Box>
      )}

      {hasAccountFilter && (
        <Box style={{ minWidth: 180 }}>
          <Select
            aria-label="Filter by account"
            size="small"
            placeholder="Account"
            selectionMode="multiple"
            options={accountOptions}
            value={selectedAccounts}
            onChange={value => setSelectedAccounts(toKeyList(value))}
          />
        </Box>
      )}
    </Flex>
  );
}
